import {
  Call_Status,
  CallInfo,
  CallMember,
  CallMember_Status,
  CallMemberDevice,
  CallRecording_Type,
  CallUiConfiguration,
  CallUiConfiguration_ParticipantGridDisplayMode,
  CallUiConfiguration_ParticipantsControlType,
  CallUiConfiguration_PermissionsSectionDisplayMode,
  CallUiConfiguration_PreviewDisplayMode,
  CallMember_Role,
  CallUiConfiguration_MemberListDisplayMode,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_model_pb';
import {
  RealtimeCallEvent_CallChanged,
  RealtimeCallEvent_CallRecordingStarted,
  RealtimeCallEvent_CallRecordingStopped,
  RealtimeCallEvent_MemberChanged,
  RealtimeCallEvent_MemberForceFinished,
  RealtimeCallEvent_MemberForceMuted,
  RealtimeCallEvent_MemberRoleChanged,
  RealtimeCallEvent_MemberWasAdded,
  RealtimeCallEvent_MemberWasRemoved,
  RealtimeCallEvent_WaitingRoomChanged,
  RealtimeCallEvent_WaitingRoomMemberChanged,
  RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_realtime_events_pb';
import {
  UpdateWaitingRoomForAllRequest_Status,
  UpdateWaitingRoomForMemberRequest_Status,
  WaitingRoomAccessRequest_Request,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_requests_pb';

import {
  Call_FinishReason,
  VideoQualitySettings,
  VideoResolution,
} from '../../WsApi/wsCallsApi/types/call/call_model_pb';
import {
  StatusCallFinishReason,
  CallMemberStatus,
  CallStatus,
  ICallInfo,
  ICallMember,
  ICallMemberChanged,
  ICallMemberDevice,
  ICallMemberForceFinished,
  ICallMemberForceMuted,
  ICallMemberWasRemoved,
  ICallRecordingStarted,
  ICallRecordingStopped,
  ICallUIFeatures,
  IMemberRoleChanged,
  IStatusCallChanged,
  IVideoQualitySettings,
  IWaitingRoomChanged,
  IWaitingRoomMember,
  MemberListDisplayMode,
  ParticipantGridDisplayMode,
  ParticipantsControlType,
  PermissionsSectionDisplayMode,
  PreviewDisplayMode,
  RecordingType,
  WaitingRoomAccessStatus,
  WaitingRoomMemberChangedStatus,
  WaitingRoomRequestStatus,
  CallVideoResolution,
} from '../callModel.types';
import { CallMemberRole } from '../callPermissions/callPermissionsModel.types';

export const callB2bRecordingTypeResponseMap: Record<RecordingType, CallRecording_Type> = {
  [RecordingType.Audio]: CallRecording_Type.AUDIO,
  [RecordingType.AudioVideo]: CallRecording_Type.AUDIO_VIDEO,
};

export const callB2bWaitingRoomMemberStatusResponseMap: Record<
  RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus,
  WaitingRoomMemberChangedStatus
> = {
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.INITIATED]:
    WaitingRoomMemberChangedStatus.Initiated,
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.APPROVED]:
    WaitingRoomMemberChangedStatus.Approved,
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.REJECTED]:
    WaitingRoomMemberChangedStatus.Rejected,
  [RealtimeCallEvent_WaitingRoomMemberChanged_RequestStatus.REQUESTED]:
    WaitingRoomMemberChangedStatus.Requested,
};

export const callB2bWaitingRoomMemberStatusRequestMap: Record<
  WaitingRoomRequestStatus,
  UpdateWaitingRoomForMemberRequest_Status
> = {
  [WaitingRoomRequestStatus.Reject]: UpdateWaitingRoomForMemberRequest_Status.REJECT,
  [WaitingRoomRequestStatus.Approve]: UpdateWaitingRoomForMemberRequest_Status.APPROVE,
};

export const callB2bWaitingRoomAllStatusRequestMap: Record<
  WaitingRoomRequestStatus,
  UpdateWaitingRoomForAllRequest_Status
> = {
  [WaitingRoomRequestStatus.Reject]: UpdateWaitingRoomForAllRequest_Status.REJECT,
  [WaitingRoomRequestStatus.Approve]: UpdateWaitingRoomForAllRequest_Status.APPROVE,
};

export const callB2bWaitingRoomAccessStatusRequestMap: Record<
  WaitingRoomAccessStatus,
  WaitingRoomAccessRequest_Request
> = {
  [WaitingRoomAccessStatus.AskToJoin]: WaitingRoomAccessRequest_Request.ASK_TO_JOIN,
  [WaitingRoomAccessStatus.Cancel]: WaitingRoomAccessRequest_Request.CANCEL,
};

export const callB2bParticipantsControlTypeMap: Record<
  CallUiConfiguration_ParticipantsControlType,
  ParticipantsControlType
> = {
  [CallUiConfiguration_ParticipantsControlType.DISABLED]: ParticipantsControlType.Disabled,
  [CallUiConfiguration_ParticipantsControlType.TOTAL_NUMBER]: ParticipantsControlType.TotalNumber,
  [CallUiConfiguration_ParticipantsControlType.WITH_MAX_NUMBER]:
    ParticipantsControlType.WithMaxNumber,
};

export const callB2bParticipantGridDisplayModeMap: Record<
  CallUiConfiguration_ParticipantGridDisplayMode,
  ParticipantGridDisplayMode
> = {
  [CallUiConfiguration_ParticipantGridDisplayMode.ADMIN]: ParticipantGridDisplayMode.Admin,
  [CallUiConfiguration_ParticipantGridDisplayMode.ALL]: ParticipantGridDisplayMode.All,
};

export const callB2bPreviewDisplayModeMap: Record<
  CallUiConfiguration_PreviewDisplayMode,
  PreviewDisplayMode
> = {
  [CallUiConfiguration_PreviewDisplayMode.DEFAULT_PREVIEW]: PreviewDisplayMode.DefaultPreview,
  [CallUiConfiguration_PreviewDisplayMode.WEBINAR_PREVIEW]: PreviewDisplayMode.WebinarPreview,
};

export const callB2bPermissionsSectionDisplayModeMap: Record<
  CallUiConfiguration_PermissionsSectionDisplayMode,
  PermissionsSectionDisplayMode
> = {
  [CallUiConfiguration_PermissionsSectionDisplayMode.CUSTOM_PERMISSIONS]:
    PermissionsSectionDisplayMode.CustomPermissions,
  [CallUiConfiguration_PermissionsSectionDisplayMode.WEBINAR_SPEAKERS]:
    PermissionsSectionDisplayMode.WebinarSpeakers,
};

export const callB2bMemberListDisplayModeMap: Record<
  CallUiConfiguration_MemberListDisplayMode,
  MemberListDisplayMode
> = {
  [CallUiConfiguration_MemberListDisplayMode.DEFAULT]: MemberListDisplayMode.Default,
  [CallUiConfiguration_MemberListDisplayMode.NAME_WITH_ROLE]: MemberListDisplayMode.NameWithRole,
};

export const callResolutionResponseMap: Record<VideoResolution, CallVideoResolution> = {
  [VideoResolution.P360]: CallVideoResolution.P360,
  [VideoResolution.P480]: CallVideoResolution.P480,
  [VideoResolution.P720]: CallVideoResolution.P720,
  [VideoResolution.P1080]: CallVideoResolution.P1080,
};

export function mapCallB2bMemberStatus(status: CallMember_Status): CallMemberStatus {
  switch (status) {
    case CallMember_Status.INITIATED:
      return CallMemberStatus.NotOnCall;
    case CallMember_Status.JOINED:
      return CallMemberStatus.OnCall;
    case CallMember_Status.DECLINED:
      return CallMemberStatus.Declined;
    case CallMember_Status.LEFT:
      return CallMemberStatus.Left;
    default:
      return CallMemberStatus.NotOnCall;
  }
}

export const mapCallB2bMemberDevice = (
  item: CallMemberDevice,
  status: CallMember_Status
): ICallMemberDevice => {
  return {
    deviceId: item.deviceId!,
    shareScreenUid: item.screenId,
    providerUserUid: item.cameraId,
    status: mapCallB2bMemberStatus(status),
  };
};

export const mapCallB2bMember = (item: CallMember): ICallMember => {
  return {
    providerUserUid: item.device!.cameraId,
    shareScreenUid: item.device!.screenId,
    avatarUrl: item.displayData!.avatarUrl,
    hasPermissionRequest: false,
    isHandRaised: false,
    handRaisedAt: null,
    platform: undefined,
    status: mapCallB2bMemberStatus(item.status),
    userId: item.memberId.toString(),
    username: item.displayData!.userName,
    isAdmin: item.isAdmin,
    device: item.device?.deviceId ? mapCallB2bMemberDevice(item.device, item.status) : null,
  };
};

export const mapCallB2bMembers = (data: CallMember[]): ICallMember[] => {
  return data.map((item) => {
    return mapCallB2bMember(item);
  });
};

export const mapCallUiFeatures = (data: CallUiConfiguration): ICallUIFeatures => {
  return {
    ...data,
    participantsControlType: callB2bParticipantsControlTypeMap[data.participantsControlType],
    participantGridDisplayMode:
      callB2bParticipantGridDisplayModeMap[data.participantGridDisplayMode],
    previewDisplayMode: callB2bPreviewDisplayModeMap[data.previewDisplayMode],
    permissionsSectionDisplayMode:
      callB2bPermissionsSectionDisplayModeMap[data.permissionsSectionDisplayMode],
    memberListDisplayMode: callB2bMemberListDisplayModeMap[data.memberListDisplayMode],
    showDurationLimitReachedBanner: data.showDurationLimitReachedBanner,
    showCallWillEndSoonBanner: data.showCallWillEndSoonBanner,
    showParticipantsLimitWillReachSoonBanner: data.showParticipantsLimitWillReachSoonBanner,
    showParticipantsRestrictedLimitReachedBanner: data.showParticipantsRestrictedLimitReachedBanner,
  };
};

export const mapCallB2bQualitySettings = (
  qualitySettings: VideoQualitySettings
): IVideoQualitySettings | null => {
  if (!qualitySettings.high) {
    return null;
  }

  return {
    low: {
      maxBitrate: qualitySettings.low!.maxBitrate,
      maxFramerate: qualitySettings.low!.maxFps,
      resolution: callResolutionResponseMap[qualitySettings.low!.resolution],
    },
    medium: {
      maxBitrate: qualitySettings.medium!.maxBitrate,
      maxFramerate: qualitySettings.medium!.maxFps,
      resolution: callResolutionResponseMap[qualitySettings.medium!.resolution],
    },
    high: {
      maxBitrate: qualitySettings.high!.maxBitrate,
      maxFramerate: qualitySettings.high!.maxFps,
      resolution: callResolutionResponseMap[qualitySettings.high!.resolution],
    },
  };
};
export const mapCallB2bInfo = (info: CallInfo): ICallInfo => {
  const { call, initiatorId, selfData } = info;

  return {
    channelId: call!.channelId,
    recordingId: call!.actualRecordingId,
    audioVideoToken: selfData!.cameraToken,
    shareScreenToken: selfData!.screenToken,
    audioVideoUid: selfData!.member!.device!.cameraId,
    shareScreenUid: selfData!.member!.device!.screenId,
    initiatorId: String(initiatorId),
    isAdmin: selfData!.member!.isAdmin,
    currentTs: String(Date.now()),
    members: mapCallB2bMembers([selfData!.member!]),
    callName: call!.callName || '',
    deviceId: selfData!.member!.device!.deviceId!,
    uiFeatures: mapCallUiFeatures(selfData!.callUiConfiguration!),

    callVisibleDuration: Number(call!.settings?.callVisibleDurationMs),
    waitingRoomEnabled: call!.settings!.waitingRoomEnabled,
    guestsAllowed: call!.settings!.guestsAllowed,

    maxBroadcasters: call!.settings!.maxBroadcasters,
    maxParticipants: call!.settings!.maxParticipants,
    maxParticipantsReminder: call!.settings!.maxParticipantsReminder,
    maxDuration: Number(call!.settings!.maxDurationMs),
    maxDurationReminder: Number(call!.settings!.maxDurationReminderMs),
    approxDurationMs: Number(call!.approxDurationMs),
    videoQualitySettings: call!.settings!.videoQualitySettings
      ? mapCallB2bQualitySettings(call!.settings!.videoQualitySettings)
      : null,
  };
};

export const mapB2bCallStatus = (status: Call_Status): CallStatus => {
  switch (status) {
    case Call_Status.UNANSWERED:
      return CallStatus.Initiated;
    case Call_Status.IN_PROGRESS:
      return CallStatus.Started;
    default:
      return CallStatus.Finished;
  }
};

const mapB2bStatusCallChangedFinishReasonMap: Record<Call_FinishReason, StatusCallFinishReason> = {
  [Call_FinishReason.DURATION_LIMIT_REACHED]: StatusCallFinishReason.DurationLimitReached,
};

export const mapB2bStatusCallChanged = (
  data: RealtimeCallEvent_CallChanged
): IStatusCallChanged => {
  return {
    status: mapB2bCallStatus(data.status),
    waitingRoomEnabled: data.settings!.waitingRoomEnabled,
    guestsAllowed: data.settings!.guestsAllowed,
    uiFeatures: mapCallUiFeatures(data.callUiConfiguration!),
    statusCallFinishReason:
      typeof data.finishReason !== 'undefined'
        ? mapB2bStatusCallChangedFinishReasonMap[data.finishReason]
        : undefined,
  };
};

export const mapB2bMemberChanged = (data: RealtimeCallEvent_MemberChanged): ICallMemberChanged => {
  return {
    memberId: data.memberId.toString(),
    status: mapCallB2bMemberStatus(data.status),
    deviceId: data.deviceId.toString(),
  };
};

export const mapB2bRecordingStarted = (
  data: RealtimeCallEvent_CallRecordingStarted
): ICallRecordingStarted => {
  return {
    recordingId: data.recordingId,
    recorderId: data.memberId.toString(),
  };
};

export const mapB2bRecordingStopped = (
  data: RealtimeCallEvent_CallRecordingStopped
): ICallRecordingStopped => {
  return {
    recordingId: data.recordingId,
  };
};

export const mapB2bMemberForceMuted = (
  data: RealtimeCallEvent_MemberForceMuted
): ICallMemberForceMuted => {
  return {
    initiatorId: data.initiatorId.toString(),
    targetMemberId: data.targetMemberId.toString(),
    deviceId: data.deviceId ? data.deviceId.toString() : null,
  };
};

export const mapB2bMemberForceFinished = (
  data: RealtimeCallEvent_MemberForceFinished
): ICallMemberForceFinished => {
  return {
    initiatorId: data.initiatorId.toString(),
    targetMemberId: data.targetMemberId.toString(),
    deviceId: data.deviceId ? data.deviceId.toString() : null,
  };
};

export const mapB2bMemberWasRemoved = (
  data: RealtimeCallEvent_MemberWasRemoved
): ICallMemberWasRemoved => {
  return {
    memberId: data.targetMemberId.toString(),
    initiatorId: data.initiatorId.toString(),
  };
};
export const mapB2bMemberWasAdded = (data: RealtimeCallEvent_MemberWasAdded): ICallMember => {
  return mapCallB2bMember(data.member!);
};

export const mapB2bWaitingRoomMember = (
  data: RealtimeCallEvent_WaitingRoomMemberChanged
): IWaitingRoomMember => {
  return {
    memberId: String(data.memberId),
    displayData: data.displayData!,
    status: callB2bWaitingRoomMemberStatusResponseMap[data.status],
  };
};

export const mapB2bWaitingRoomChanged = (
  data: RealtimeCallEvent_WaitingRoomChanged
): IWaitingRoomChanged => {
  return {
    requested: data.requested.map(mapB2bWaitingRoomMember),
  };
};

export const memberRoleMap: Record<CallMember_Role, CallMemberRole> = {
  [CallMember_Role.SUPERUSER]: CallMemberRole.Superuser,
  [CallMember_Role.MEMBER]: CallMemberRole.Member,
  [CallMember_Role.GUEST]: CallMemberRole.Guest,
};

export const mapMemberRoleChanged = (
  data: RealtimeCallEvent_MemberRoleChanged
): IMemberRoleChanged => {
  return {
    userId: String(data.targetMemberId),
    role: memberRoleMap[data.role],
  };
};
